import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { getWorkspace, updateLayout as updateLayoutApi } from '../../../services/workspaceService';
import { getDataCapture } from '../../../services/dataCaptureService';
import { migrateWorkspace, needsMigration } from './workspaceMigration';

const byStartTime = (a, b) => (a.startTime || 0) - (b.startTime || 0);

/**
 * Decide which tab a freshly-opened workspace shows: the one the owner marked
 * default, otherwise the first. An activeTabId already in the config wins,
 * because it is where the last session left off.
 */
function withOpeningTab(layoutConfig) {
  const lc = layoutConfig || { tabs: [], widgets: [] };
  if (lc.activeTabId) return lc;
  if (lc.defaultTabId) return { ...lc, activeTabId: lc.defaultTabId };
  if (lc.tabs?.length > 0) return { ...lc, activeTabId: lc.tabs[0].id };
  return lc;
}

/**
 * Loads the workspace and its recordings once, migrating a pre-tab workspace on
 * the way through, and hands the pieces to whichever hook owns them.
 *
 * A recording that fails to load is dropped rather than failing the page: one
 * deleted capture should not make the whole workspace unopenable.
 */
export default function useWorkspaceLoader({ workspaceId, navigate, hydrate }) {
  const [workspace, setWorkspace] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWorkspace = useCallback(async () => {
    try {
      let ws = await getWorkspace(workspaceId);

      if (needsMigration(ws)) {
        ws = migrateWorkspace(ws);
        await updateLayoutApi(workspaceId, ws.layoutConfig).catch(() => {});
      }

      setWorkspace(ws);
      hydrate(ws, withOpeningTab(ws.layoutConfig));

      const loaded = await Promise.all(
        (ws.recordingIds || []).map((id) => getDataCapture(id).catch(() => null))
      );
      setRecordings(loaded.filter(Boolean).sort(byStartTime));
      setLoading(false);
    } catch (err) {
      toast.error('Failed to load workspace');
      navigate('/dashboard/workspaces');
    }
    // `hydrate` is rebuilt every render by its caller; re-running the fetch on
    // that identity would loop. The workspace id is the only real input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, navigate]);

  useEffect(() => {
    fetchWorkspace();
  }, [fetchWorkspace]);

  return { workspace, setWorkspace, recordings, setRecordings, loading };
}
